<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import Button from "../Button.svelte";
  import {
    uploadFile,
    saveUploadedFileToUserSettings,
    completeFileUpload,
    validateSelectedFile
  } from "../../lib/helpers/steps/uploadFile";
  import { settingsStore } from "../../lib/stores/settings";
  import ImageUploader from "../ImageUploader.svelte";
  import { TypographyType } from "../../lib/constants/typographyMap";
  import Typography from "../Typography.svelte";
  import Youtube from "../Youtube.svelte";
  import bytes from "bytes";
  import { MAX_FILE_SIZES } from "../../lib/constants/upload";

  export let currentRoute;

  let file: File | null;
  let isDisabled: boolean = true;
  let errorMessage = "";
  let isBusy: boolean = false;
  let headerCopy: string;
  let subHeaderCopy: string;
  let validFileTypes: string;

  const handleContinue = async () => {
    try {
      isBusy = true;

      const fileKey = await uploadFile(file as File);
      await saveUploadedFileToUserSettings(fileKey);
      completeFileUpload();
    } catch (error) {
      isBusy = false;
      errorMessage = generalCopy?.fatalErrorCopy;
    }
  };

  const handleUploadedFile = (e: Event) => {
    const targetElement = e.target as HTMLInputElement;
    const selectedFile = targetElement.files?.[0] as File;

    try {
      validateSelectedFile(selectedFile);
      file = selectedFile;
      isDisabled = false;
      errorMessage = "";
    } catch (error) {
      const err = error as Error;
      const cause = err.cause as { type: string, mimeType: string, maxFileSize: number };

      if (cause.type === "MAX_FILE_SIZE") {
        errorMessage = copy.maxFileSizeErrorCopy.replace("$fileSize", `${bytes(cause.maxFileSize)}`);
      } else {
        errorMessage = copy.invalidFileErrorCopy.replace("$fileType", validFileTypes);
      }
      isDisabled = true;
      file = null;
    }
  };

  $: copy = $getCopy("uploadFile") as Record<string, string>;
  $: requiresUpload = $settingsStore?.requiresUpload || false;
  $: generalCopy = $getCopy("general", { basePath: "" }) as Record<string, string>;
  $: imageUploaderButtonCopy = file? copy.updateFileButtonCopy: copy.selectFileButtonCopy;
  $: {
    const validMimeTypes = $settingsStore.uploadMimeTypes?.map((mType) => `.${mType.split("/").pop()}`);
    const lastValidMimeType = validMimeTypes?.pop();

    validFileTypes = `${validMimeTypes?.join(", ")} or ${lastValidMimeType}`;
  }
  $: areVideoUploadsAllowed = $settingsStore.uploadMimeTypes?.includes("video/mp4") || false;
  $: areImageUploadsAllowed = $settingsStore.uploadMimeTypes?.includes("image/jpeg") || false;
  $: {
    const imageCopy = `an image (max size ${bytes(MAX_FILE_SIZES.image)})`;
    const videoCopy = `a video (max size ${bytes(MAX_FILE_SIZES.video)})`;
    const subHeaderCopyArray = [];
    if(areImageUploadsAllowed && areVideoUploadsAllowed) {
      subHeaderCopyArray.push(`${imageCopy} or ${videoCopy}`);
    } else {
      subHeaderCopyArray.push(areImageUploadsAllowed? imageCopy: videoCopy);
    }

    subHeaderCopy = copy.subHeaderCopy.replace("$fileMaxSizeCopy", subHeaderCopyArray.join(" or "));
  }
  $: {
    if (areVideoUploadsAllowed && areImageUploadsAllowed) {
      headerCopy = copy.headerCopy.replace("$fileType", "video or image");
    } else {
      headerCopy = copy.headerCopy.replace("$fileType", areVideoUploadsAllowed? "video": "image");
    }
  }
</script>

<BaseStep {currentRoute}>
  <svelte:fragment slot="content">
    <SharedHeading {headerCopy} {subHeaderCopy} />

    <div class="flex flex-col lg:flex-row mt-4 lg:mt-8 gap-4">
      <div class="w-full lg:w-fit">
        <ImageUploader bind:file {handleUploadedFile} selectFileLabel={imageUploaderButtonCopy} />
        <div class="mt-4">
          <Typography
            mobile={TypographyType.Body2Regular}
            desktop={TypographyType.Body2Medium}
            --font-color="var(--color-red-50)">
            <span aria-label="error-message">{errorMessage}</span>
          </Typography>
        </div>
      </div>
    </div>

    <div class="mt-4 lg:mt-10 flex flex-col lg:flex-row gap-4 lg:gap-8">
      <Button class="w-full lg:w-fit" primary on:click={handleContinue} disabled={isDisabled || isBusy} {isBusy}>
        {copy.continueButtonCopy}
      </Button>

      {#if !requiresUpload}
        <Button class="w-full lg:w-fit" secondary on:click={completeFileUpload} disabled={isBusy}>
          {copy.skipButtonCopy}
        </Button>
      {/if}
    </div>

    <Youtube videoId={copy.videoId} />
  </svelte:fragment>
</BaseStep>
